import React from 'react'
import { IconButton, Tooltip } from '@mui/material'
import EditOutlined from '@mui/icons-material/EditOutlined'
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined'
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined'
import { Campaign } from '@marketing-milk/interfaces'
import { ActionType } from '../campaign-page/CampaignContent'
import { campaignHasError } from '../../helpers/campaign.helpers'

type Props = {
  campaign: Campaign
  action: (type: ActionType) => void
}

type RowActionProps = {
  label: string
  /** When set, the action is unavailable and this is shown in place of the label. */
  disabledReason?: string
  icon: React.ReactNode
  onClick: () => void
}

// A disabled MUI button swallows pointer events, so the tooltip needs a wrapper to listen on —
// otherwise the explanation for the disabled state never appears, which is the point of it.
const RowAction = ({ label, disabledReason, icon, onClick }: RowActionProps) => (
  <Tooltip title={disabledReason ?? label}>
    <span>
      <IconButton
        size="small"
        color="primary"
        aria-label={disabledReason ? `${label} (unavailable): ${disabledReason}` : label}
        disabled={!!disabledReason}
        onClick={onClick}
      >
        {icon}
      </IconButton>
    </span>
  </Tooltip>
)

export const CampaignTools = ({ campaign, action }: Props) => {
  // Campaigns built in Facebook Ads Manager have no stored setup here, which is what every
  // action below needs.
  const notOursReason = campaign.config
    ? undefined
    : 'This campaign was built in Facebook Ads Manager, so its setup is not stored here.'

  // An active campaign can always be duplicated. An inactive one only if its RoAS is 10 or better.
  const canDuplicate = (campaign.roas || 0) >= 10 || campaign.status === 'ACTIVE'
  const duplicateReason =
    notOursReason ??
    (canDuplicate
      ? undefined
      : 'Only active campaigns, or ones with a RoAS of 10 or better, can be duplicated.')

  // Retry opens the same drawer and submits the same PATCH as an ordinary edit, so a failed
  // campaign gets the same control under a label that says what it is for.
  const hasError = campaignHasError(campaign)

  return (
    <div className="flex items-center justify-end">
      <RowAction
        label={hasError ? 'Fix and resubmit campaign' : 'Edit campaign'}
        disabledReason={notOursReason}
        icon={<EditOutlined fontSize="small" />}
        onClick={() => action(hasError ? 'retry' : 'edit')}
      />
      <RowAction
        label="Duplicate campaign"
        disabledReason={duplicateReason}
        icon={<ContentCopyOutlined fontSize="small" />}
        onClick={() => action('duplicate')}
      />
      <RowAction
        label="Delete campaign"
        disabledReason={notOursReason}
        icon={<DeleteOutlineOutlined fontSize="small" />}
        onClick={() => action('delete')}
      />
    </div>
  )
}
